import SftGlobalData from '../../library/SftGlobalData';

/**
 * Shared client for the auth report pipeline (reports&action=execute →
 * status polling) and the layout/logo fetch a document preview needs.
 * Used by the reports module and by Uploads › Documentlayouts.
 */

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * "12, 13 ,14" → ['12','13','14'] (MyFashionManager multi-select: a comma
 * list per {RecId;n} slot). Returns [] for empty input.
 */
export const splitRecIdList = (text) => String(text ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '');

/**
 * Client-side mirror of the server rule: every used slot needs a value, and
 * lists of different lengths only combine when the shorter one has length 1.
 * Returns '' when valid, else a key of the problem ('missing' | 'length').
 */
export const validateRecIds = (slots, valuesBySlot) => {
    let rows = 1;
    for (const slot of slots) {
        const list = splitRecIdList(valuesBySlot[slot]);
        if (list.length === 0) return 'missing';
        if (list.length > 1) {
            if (rows === 1) rows = list.length;
            else if (list.length !== rows) return 'length';
        }
    }
    return '';
};

/**
 * Run a report to completion. Resolves with the final status payload
 * (status 'done'), rejects with an Error carrying the server message.
 * onProgress(statusPayload) is called on every poll.
 */
export const runReportPipeline = async ({
    api, sftUserId, repid, recids = {}, storeId = '', posId = '', lang = '',
    variables = {}, withDatasets = true, onProgress = null, isCancelled = null,
}) => {
    const start = await SftGlobalData.post('reports&action=execute', {
        key: api,
        sft_user_id: sftUserId || '',
        repid,
        variables,
        recids,
        store_id: storeId,
        pos_id: posId,
        lang,
    });
    if (!start?.success || !start?.data?.run_id) {
        throw new Error(start?.message || 'Report could not be started');
    }
    const runId = start.data.run_id;

    for (let attempt = 0; ; attempt++) {
        if (isCancelled && isCancelled()) {
            SftGlobalData.post('reports&action=cancel', { key: api, run_id: runId });
            throw new Error('cancelled');
        }
        await sleep(attempt === 0 ? 700 : (attempt < 10 ? 1000 : (attempt < 30 ? 2000 : 5000)));
        const response = await SftGlobalData.post('reports&action=status', {
            key: api, run_id: runId, with_datasets: withDatasets ? 1 : 0,
        });
        if (!response?.success || !response?.data) {
            throw new Error(response?.message || 'Report status unavailable');
        }
        const data = response.data;
        if (onProgress) onProgress(data);
        if (data.status === 'done') return data;
        if (data.status === 'error') throw new Error(data.error || 'Report failed');
        if (data.status === 'cancelled') throw new Error('cancelled');
    }
};

/** Datasets of an already finished run (rows included). */
export const fetchRunDatasets = async ({ api, runId }) => {
    const response = await SftGlobalData.post('reports&action=status', {
        key: api, run_id: runId, with_datasets: 1,
    });
    if (!response?.success || !response?.data) {
        throw new Error(response?.message || 'Report status unavailable');
    }
    return response.data;
};

const base64ToBytes = (b64) => {
    const binary = atob(b64 || '');
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
};

/**
 * .fr3 bytes → text, honouring the XML prolog's encoding (FastReport
 * writes utf-8 or a legacy 8-bit code page such as windows-1252).
 */
export const decodeFr3 = (bytes) => {
    const head = new TextDecoder('ascii').decode(bytes.slice(0, 200));
    const match = /encoding\s*=\s*["']([^"']+)["']/i.exec(head);
    const declared = (match ? match[1] : 'utf-8').toLowerCase();
    try {
        return new TextDecoder(declared).decode(bytes);
    } catch (e) {
        return new TextDecoder('windows-1252').decode(bytes);
    }
};

/**
 * Everything renderReportPreview() needs, from a finished run: each
 * printable dataset joined with its layout (fetched once per name), plus
 * the logo the tablet would print for this document kind/scope.
 * Datasets whose template is missing or .frf come back with `skipped`.
 */
export const buildPreviewInput = async ({
    api, status, documentKind = 'ticket', storeId = '', posId = '', title = '', lang = '', variables = null,
}) => {
    const cache = {};
    let logo = null;
    let logoFetched = false;
    const datasets = [];

    for (const ds of (status.datasets || [])) {
        const template = (ds.template || ds.repform || '').trim();
        const entry = {
            step: ds.step,
            type: ds.type,
            template,
            columns: ds.columns || [],
            rows: ds.rows || [],
            binaryColumns: ds.binary_columns || [],
            rowCount: ds.row_count ?? (ds.rows || []).length,
            truncated: !!ds.truncated,
            templateXml: null,
            templateBytes: null,
            skipped: null,
        };
        if (template === '') {
            entry.skipped = 'no_layout';
        } else if (/\.frf$/i.test(template)) {
            entry.skipped = 'frf';
        } else {
            if (!cache[template]) {
                const response = await SftGlobalData.post('reports&action=fetchLayout', {
                    key: api,
                    name: template,
                    with_logo: logoFetched ? 0 : 1,
                    document_kind: documentKind,
                    store_id: storeId,
                    pos_id: posId,
                });
                if (response?.success && response.data?.content_base64) {
                    const bytes = base64ToBytes(response.data.content_base64);
                    cache[template] = { bytes, xml: decodeFr3(bytes) };
                    if (!logoFetched) {
                        logo = response.data.logo || null;
                        logoFetched = true;
                    }
                } else {
                    cache[template] = { error: response?.message || 'Layout not found' };
                }
            }
            if (cache[template].error) {
                entry.skipped = 'layout_missing';
                entry.error = cache[template].error;
            } else {
                entry.templateBytes = cache[template].bytes;
                entry.templateXml = cache[template].xml;
            }
        }
        datasets.push(entry);
    }

    /**
     * Parent of an inherited layout (tl_portrait.fr3 / tl_landscape.fr3): auth
     * only hands it out as the parent of a layout this client owns.
     */
    const fetchParent = async (name, childTemplate) => {
        const response = await SftGlobalData.post('reports&action=fetchLayout', {
            key: api,
            name,
            parent_of: childTemplate || '',
            with_logo: 0,
        });
        if (response?.success && response.data?.content_base64) {
            return base64ToBytes(response.data.content_base64);
        }
        return null;
    };

    // Report header variables: the run's (status.header_vars: RepTitle,
    // RepLicenceName, VarBetmN, VarCodeNOms, VarLevartNOms) plus the
    // caller's (RepSelection from the run dialog), the caller winning.
    const merged = { ...((status && status.header_vars) || {}), ...(variables || {}) };

    return { datasets, logo, title, lang, variables: merged, fetchParent };
};
